/* eslint-disable @typescript-eslint/no-empty-function */
import { useAlert } from 'components/alert/AlertStack';
import { classNames, useEffect, useQuery, useState } from 'libs/react';
import { Content } from 'components/Content';
import { Screen } from 'components/Screen';
import { React } from 'libs/react';
import { useDomain } from 'components/domain/DomainProvider';
import { MutationForm } from 'components/form/graphql/MutationForm';
import { Icon } from 'components/Icon';
import { Text } from 'components/Text';
import { Button } from 'components/Button';
import { CreateDealMutation } from 'graphql/Deal/mutations/CreateDealMutation';
import { loadCampaignAttribution } from 'components/domain/campaignAttribution';
import { pushEnhancedConversionUserData } from 'components/domain/gtmConsent';
import { gql } from '@apollo/client';
import { PartnerFormQuery } from 'graphql/Client/queries/PartnerFormQuery';
import { FieldGroupFragment } from 'graphql/FieldGroup/fragments/FieldGroupFragment';
import { isUnitAnswerEmpty } from 'modules/public/store/storeVoucherFields';
import { ContactSection } from './DynamicSalesFormContact';
import { buildGiftDealInput, validateGiftDealInput } from 'modules/public/store/storeGift';
import { FieldSection } from './DynamicSalesFormField';
import { ProductSection } from './DynamicSalesFormProduct';
import { PublicLegalConsent } from 'components/public/PublicLegalConsent';
import { getConfigurationFieldValue } from 'helpers/ConfigurationField';
import { EmptyData } from 'components/EmptyData';
import { isPublicAccountUnavailableError } from 'helpers/isPublicAccountUnavailableError';
import { CaptchaWidget, isCaptchaValid } from 'components/captcha/CaptchaWidget';
import { Header } from 'components/Header';
import { DynamicSalesFormHeader } from './DynamicSalesFormHeader';
import { useParams, useLocation } from 'react-router-dom';
import i18n from 'components/translation/i18n';
import './DynamicSalesFormScreen.scss';
import { applyBrandColors, resolvePrimaryColor } from 'helpers/primaryColor';
/**
 * @const DynamicSalesFormSaleItemFragment
 * @since 1.0.0
 */
const DynamicSalesFormSaleItemFragment = gql `
  fragment DynamicSalesFormSaleItem on SaleItem {
    id
    sku
    title
    infos
    summary
    configuration
    price
    formFieldGroupIds

    image {
      image {
        url
        thumbnail
        thumbnailWidth
        display
        displayWidth
      }
    }

    translations {
      language {
        code
      }
      title
      infos
      summary
    }

    promotions {
      type
      value
    }
  }
`;
/**
 * The form's DEAL groups. A group linked to products is shown only when one
 * of them is selected.
 * @const DynamicSalesFormFieldGroupsQuery
 * @since 1.0.0
 */
const DynamicSalesFormFieldGroupsQuery = gql `
  query DynamicSalesFormFieldGroupsQuery($input: FieldGroupsInput) {
    fieldGroups(input: $input) {
      ...FieldGroupFragment
      productLinked
    }
  }
  ${FieldGroupFragment}
`;
/**
 * @component DynamicSalesFormScreen
 * @since 1.0.0
 */
export function DynamicSalesFormScreen() {
    var _a, _b, _c, _d, _e, _f;
    const alert = useAlert();
    const { lang, partnerSlug: partnerSlugParam } = useParams();
    const partnerSlug = (partnerSlugParam === null || partnerSlugParam === void 0 ? void 0 : partnerSlugParam.toLowerCase()) || null;
    const location = useLocation();
    const isStoreMode = location.pathname.startsWith('/store');
    const [termsChecked, setTermsChecked] = useState(false);
    const [marketingOptIn, setMarketingOptIn] = useState(false);
    const [captchaToken, setCaptchaToken] = useState(null);
    const [addressFilled, setAddressFilled] = useState(false);
    const [formSent, setFormSent] = useState(false);
    const [dealId, setDealId] = useState('');
    const [dealNumber, setDealNumber] = useState('');
    const [email, setEmail] = useState('');
    const [selectedSaleItemIds, setSelectedSaleItemIds] = useState([]);
    // Set primary color from domain
    const { host, logo: domainLogo, icon, options, configurationFields, captcha } = useDomain();
    // Partner form (/form/<lang>/p/<slug>): partner catalog, logo and quote
    const [partnerData, partnerReady, partnerError] = useQuery(PartnerFormQuery, { variables: { input: { slug: partnerSlug !== null && partnerSlug !== void 0 ? partnerSlug : '' } }, skip: !partnerSlug });
    const partnerForm = partnerSlug ? ((_a = partnerData === null || partnerData === void 0 ? void 0 : partnerData.partnerForm) !== null && _a !== void 0 ? _a : null) : null;
    const logo = (_b = partnerForm === null || partnerForm === void 0 ? void 0 : partnerForm.image) !== null && _b !== void 0 ? _b : domainLogo;
    applyBrandColors({
        primary: resolvePrimaryColor(options.color, '#F05B22'),
        accent: options.accentColor,
        surface: options.surfaceColor
    });
    // change favicon with logo
    useEffect(() => {
        var _a;
        const favicon = document.querySelector('link[rel="icon"]');
        if (favicon) {
            favicon.href = (_a = icon === null || icon === void 0 ? void 0 : icon.url) !== null && _a !== void 0 ? _a : '/favicon-16x16.png';
        }
    }, [icon === null || icon === void 0 ? void 0 : icon.url]);
    // TMP - Hide group title
    const [hideGroupTitle, setHideGroupTitle] = useState(true);
    useEffect(() => {
        if (host === 'knapper.centralio.com') {
            setHideGroupTitle(false);
        }
    }, [host]);
    useEffect(() => {
        document.body.classList.toggle('loaded', true);
    }, []);
    let language = lang !== null && lang !== void 0 ? lang : i18n.language.substring(0, 2);
    if (lang) {
        // force EN if not FR
        if (lang !== 'fr' && lang !== 'en') {
            language = 'en';
        }
        i18n.changeLanguage(lang);
        language = lang;
    }
    // Unknown or disabled partner form: back to the account form.
    useEffect(() => {
        if (partnerSlug && partnerReady && !partnerError && !partnerForm) {
            window.location.replace(`/form/${language}`);
        }
    }, [partnerSlug, partnerReady, partnerError, partnerForm, language]);
    const hasDynamicSalesForm = getConfigurationFieldValue(configurationFields !== null && configurationFields !== void 0 ? configurationFields : [], 'HAS_DYNAMIC_SALES_FORM');
    const hasDirectSales = getConfigurationFieldValue(configurationFields !== null && configurationFields !== void 0 ? configurationFields : [], 'HAS_DIRECT_SALES');
    const [hasOnlyFinishedProducts, setHasOnlyFinishedProducts] = useState(isStoreMode ? true : false);
    const dynamicSalesFormSaleItemGroup = getConfigurationFieldValue(configurationFields !== null && configurationFields !== void 0 ? configurationFields : [], isStoreMode
        ? 'DIRECT_SALES_FORM_SALE_ITEM_GROUP'
        : 'DYNAMIC_SALES_FORM_SALE_ITEM_GROUP');
    const dynamicSalesFormContactFields = getConfigurationFieldValue(configurationFields !== null && configurationFields !== void 0 ? configurationFields : [], isStoreMode
        ? 'DIRECT_SALES_FORM_CONTACT_FIELDS'
        : 'DYNAMIC_SALES_FORM_CONTACT_FIELDS');
    const dynamicSalesFormPriceDisplay = getConfigurationFieldValue(configurationFields !== null && configurationFields !== void 0 ? configurationFields : [], 'DYNAMIC_SALES_FORM_PRICE_DISPLAY');
    useEffect(() => {
        if (!dynamicSalesFormContactFields ||
            (dynamicSalesFormContactFields &&
                !dynamicSalesFormContactFields.includes('ADDRESS')) ||
            host === 'estrimont.centralio.com' ||
            host === 'axhotel.centralio.com') {
            setAddressFilled(true);
        }
    }, [dynamicSalesFormContactFields]);
    const [dataFieldGroup] = useQuery(DynamicSalesFormFieldGroupsQuery, {
        variables: {
            input: {
                query: [
                    {
                        field: 'category',
                        value: isStoreMode ? 'ORDER' : 'DEAL'
                    }
                ]
            }
        }
    });
    const queryParameters = new URLSearchParams(window.location.search);
    const product = (_c = queryParameters.get('product')) === null || _c === void 0 ? void 0 : _c.toLowerCase().trim();
    const category = queryParameters.get('category');
    // The config holds category ids (a single id on older configurations)
    const defaultGroups = Array.isArray(dynamicSalesFormSaleItemGroup)
        ? dynamicSalesFormSaleItemGroup.join(',')
        : (dynamicSalesFormSaleItemGroup !== null && dynamicSalesFormSaleItemGroup !== void 0 ? dynamicSalesFormSaleItemGroup : '');
    // ?category= replaces the default categories, as before
    const groups = category !== null && category !== void 0 ? category : defaultGroups;
    // The product found by ?product= is then the only one shown
    const showsOnlyProduct = !!product &&
        (options.selectionMode === 'single' ||
            options.allowSingleFallback === 'TRUE');
    const saleItemsInput = Object.assign(Object.assign({ optionConfiguration: 'ITEM_ONLY', visibility: ['GLOBAL', 'PUBLIC'], active: true, order: 'sku:ASC' }, (isStoreMode && { configuration: 'FINISHED' })), (partnerSlug && { partner: partnerSlug, partnerChannel: 'FORM' }));
    const [data, listReady, error] = useQuery(gql `
      query SaleItemsList($input: SaleItemsInput!) {
        saleItems(input: $input) {
          ...DynamicSalesFormSaleItem
        }
      }
      ${DynamicSalesFormSaleItemFragment}
    `, {
        variables: {
            input: Object.assign(Object.assign({}, saleItemsInput), (groups && { groups: groups.split(',') }))
        },
        skip: showsOnlyProduct,
        fetchPolicy: 'cache-and-network'
    });
    // A variant SKU can be passed as ?product=, so variants are only loaded here
    const [skuData, skuReady] = useQuery(gql `
      query SaleItemsBySku($input: SaleItemsInput!) {
        saleItems(input: $input) {
          ...DynamicSalesFormSaleItem

          variants {
            id
            sku
            active
            title
            price

            translations {
              language {
                code
              }
              title
              infos
            }

            image {
              url
              thumbnail
              thumbnailWidth
              display
              displayWidth
            }

            values {
              id
              value
              field {
                id
                title
                type
                options
              }
            }

            fieldValues {
              value
              field {
                id
                title
                type
                options
                promote
                group {
                  category
                }
              }
            }
          }
        }
      }
      ${DynamicSalesFormSaleItemFragment}
    `, {
        variables: {
            input: Object.assign(Object.assign({}, saleItemsInput), { sku: product })
        },
        skip: !product,
        fetchPolicy: 'cache-and-network'
    });
    const ready = listReady && skuReady;
    // Groups of the selected products, added to the default ones
    const selectedGroupIds = new Set([...((_d = data === null || data === void 0 ? void 0 : data.saleItems) !== null && _d !== void 0 ? _d : []), ...((_e = skuData === null || skuData === void 0 ? void 0 : skuData.saleItems) !== null && _e !== void 0 ? _e : [])]
        .filter((saleItem) => selectedSaleItemIds.includes(saleItem.id))
        .flatMap((saleItem) => { var _a; return (_a = saleItem.formFieldGroupIds) !== null && _a !== void 0 ? _a : []; }));
    const fieldGroups = ((_f = dataFieldGroup === null || dataFieldGroup === void 0 ? void 0 : dataFieldGroup.fieldGroups) !== null && _f !== void 0 ? _f : []).filter((group) => !group.productLinked || selectedGroupIds.has(group.id));
    const formFields = fieldGroups
        .flatMap((group) => group.fields)
        .filter((field) => field.visibility === 'DYNAMIC_SALES_FORM');
    /**
     * @function onCaptchaVerify
     * @since 1.0.0
     * @hidden
     */
    function onCaptchaVerify(token) {
        setCaptchaToken(token);
    }
    /**
     * @function onProcess
     * @since 1.0.0
     * @hidden
     */
    function onProcess(values) {
        var _a;
        if (!values.saleItems) {
            alert.present({ infos: t('Please select at least one product.') });
            throw new Error('no_product_selected');
        }
        // A deselected product's answers stay in the values: drop its groups
        const hiddenFieldIds = new Set(((_a = dataFieldGroup === null || dataFieldGroup === void 0 ? void 0 : dataFieldGroup.fieldGroups) !== null && _a !== void 0 ? _a : [])
            .filter((group) => !fieldGroups.includes(group))
            .flatMap((group) => group.fields.map((field) => field.id)));
        const fieldValues = values.fields
            ? Object.keys(values.fields)
                .filter((key) => !hiddenFieldIds.has(key))
                .map((key) => ({
                fieldId: key,
                value: JSON.stringify({ value: values.fields[key] })
            }))
            : [];
        if (formFields.some((field) => { var _a; return field.required && isUnitAnswerEmpty(field, (_a = values.fields) === null || _a === void 0 ? void 0 : _a[field.id]); })) {
            alert.present({ infos: t('Please fill in the required fields (*).') });
            throw new Error('required_fields_missing');
        }
        setEmail(values.email);
        const giftError = validateGiftDealInput(values);
        if (giftError) {
            alert.present({ infos: giftError });
            throw new Error('gift_invalid');
        }
        const data = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ type: 'QUOTE', title: hasDirectSales === 'TRUE' && hasOnlyFinishedProducts
                ? t('Direct Sale from ') +
                    (values.firstName
                        ? values.firstName + ' ' + values.lastName
                        : values.email)
                : t('Public Quote Request from ') +
                    (values.firstName
                        ? values.firstName + ' ' + values.lastName
                        : values.email), draft: false, plannedDate: values.plannedDate, discount: 0, status: 'ACTIVE', stage: 'DRAFT', client: {
                name: values.name
            }, contact: {
                email: values.email,
                firstName: values.firstName,
                lastName: values.lastName,
                phone: values.phone
            }, location: {
                civicNumber: values.civicNumber,
                road: values.road,
                apartment: values.apartment,
                city: values.city,
                province: values.province,
                postalcode: values.postalcode,
                country: values.country,
                latitude: Number(values.latitude),
                longitude: Number(values.longitude),
                billing: true,
                shipping: true,
                primary: true
            }, currency: 'ef1f4eb475d211eabc550242ac130001' }, (partnerSlug
            ? { formPartner: partnerSlug }
            : { partner: values.partner })), { language: language, items: values.saleItems.split(',').map((entry) => {
                const [item, quantity, variant] = entry.split(':');
                return Object.assign({ item, quantity: Number(quantity) }, (variant && { variant }));
            }), fieldValues: fieldValues, priceDisplay: dynamicSalesFormPriceDisplay !== null && dynamicSalesFormPriceDisplay !== void 0 ? dynamicSalesFormPriceDisplay : 'NONE', directSale: hasDirectSales === 'TRUE' && hasOnlyFinishedProducts, salesForm: !isStoreMode }), buildGiftDealInput(values)), { captchaToken: captchaToken || undefined, marketingOptIn: marketingOptIn || undefined }), loadCampaignAttribution(host));
        return {
            variables: {
                input: data
            }
        };
    }
    /**
     * @function onSuccess
     * @since 1.0.0
     *
     * @hidden
     */
    const onSuccess = (deal) => {
        var _a, _b, _c, _d;
        if (hasDirectSales === 'TRUE' && hasOnlyFinishedProducts) {
            window.location.href = '/interactive-quote/' + ((_a = deal === null || deal === void 0 ? void 0 : deal.createDeal) === null || _a === void 0 ? void 0 : _a.id);
        }
        else {
            setFormSent(true);
            setDealId((_b = deal === null || deal === void 0 ? void 0 : deal.createDeal) === null || _b === void 0 ? void 0 : _b.id);
            setDealNumber((_d = (_c = deal === null || deal === void 0 ? void 0 : deal.createDeal) === null || _c === void 0 ? void 0 : _c.number) !== null && _d !== void 0 ? _d : '');
            // add success=true to the url
            window.history.pushState({ success: true }, 'Quote sent', window.location.pathname + '?success=true');
            if (options.gtmId) {
                pushEnhancedConversionUserData(host, { email });
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({ event: 'generate_lead' });
            }
        }
    };
    /**
     * @function onFailure
     * @since 1.0.0
     * @hidden
     */
    const onFailure = (error) => {
        alert.present({ error });
    };
    const className = classNames(`dynamic-sales-form-screen`);
    const captchaConfig = captcha !== null && captcha !== void 0 ? captcha : {
        enabled: false,
        provider: 'turnstile',
        siteKey: null
    };
    const captchaValid = isCaptchaValid(captchaConfig, captchaToken);
    if (isPublicAccountUnavailableError(error) ||
        (hasDynamicSalesForm !== 'TRUE' && hasDirectSales !== 'TRUE')) {
        return (React.createElement(EmptyData, { title: t('This form is not available.'), description: t('Please contact us for more information.') }));
    }
    return (React.createElement(Screen, { className: className, ready: !partnerSlug || !!partnerForm },
        React.createElement(MutationForm, { name: "dynamic-sales-form", mutation: CreateDealMutation, onProcess: onProcess, onSuccess: onSuccess, onFailure: onFailure },
            React.createElement(Content, null,
                React.createElement(Header, null,
                    React.createElement(DynamicSalesFormHeader, { configurationFields: configurationFields, logo: logo, isStoreMode: isStoreMode })),
                !formSent ? (React.createElement(React.Fragment, null,
                    React.createElement(ProductSection, { items: data === null || data === void 0 ? void 0 : data.saleItems, skuItems: skuData === null || skuData === void 0 ? void 0 : skuData.saleItems, product: product, ready: ready, lang: language, hasDirectSales: hasDirectSales === 'TRUE', setHasOnlyFinishedProducts: setHasOnlyFinishedProducts, autoSelectSingle: !category && !!defaultGroups, isStoreMode: isStoreMode, onSelectionChange: setSelectedSaleItemIds }),
                    React.createElement(ContactSection, { contactFields: dynamicSalesFormContactFields, onAddressFilled: setAddressFilled, isStoreMode: isStoreMode, hidePartnerField: !!partnerForm, isPhoneRequired: host !== 'estrimont.centralio.com' &&
                            host !== 'axhotel.centralio.com', hideNameFields: isStoreMode &&
                            (host === 'estrimont.centralio.com' ||
                                host === 'axhotel.centralio.com'), hidePhoneField: isStoreMode &&
                            (host === 'estrimont.centralio.com' ||
                                host === 'axhotel.centralio.com') }),
                    React.createElement(FieldSection, { fieldGroups: fieldGroups, hideGroupTitle: hideGroupTitle, plannedDate: dynamicSalesFormContactFields &&
                            dynamicSalesFormContactFields.includes('PLANNED_DATE'), plannedDateMode: getConfigurationFieldValue(configurationFields !== null && configurationFields !== void 0 ? configurationFields : [], 'PLANNED_DATE_MODE') }),
                    React.createElement("div", { className: "section send-section" },
                        React.createElement("div", { className: "send-section-terms" },
                            React.createElement(PublicLegalConsent, { configurationFields: configurationFields, language: language, privacyAccepted: termsChecked, marketingOptIn: marketingOptIn, onPrivacyAcceptedChange: setTermsChecked, onMarketingOptInChange: setMarketingOptIn })),
                        termsChecked && (React.createElement(CaptchaWidget, { captcha: captchaConfig, language: language, onVerify: onCaptchaVerify })),
                        !addressFilled ? (React.createElement(Button, { style: "primary", size: "large", label: t('Fill in your address'), disabled: true })) : (React.createElement(Button, { style: "primary", size: "large", label: hasOnlyFinishedProducts && hasDirectSales === 'TRUE'
                                ? t('Review my order')
                                : t('Get my quote'), disabled: !termsChecked || !captchaValid, submit: termsChecked && captchaValid }))))) : host === 'form.clubpiscine.ca' ? (React.createElement("div", { className: "section sent-section" },
                    React.createElement("h1", null, t('Thank you for your quote request!')),
                    React.createElement("p", null,
                        t('Request '),
                        "\u00A0",
                        React.createElement("strong", null,
                            "#",
                            dealNumber),
                        "\u00A0",
                        t(' has been generated and sent to your Club Piscine store. One of our specialists will contact you within the next 48 hours.'),
                        React.createElement("br", null),
                        t('You can view the details of your request by clicking on the button below or by using the unique link sent to '),
                        "\u00A0",
                        React.createElement("strong", null,
                            React.createElement("a", { href: `mailto:${email}` }, email))),
                    React.createElement("p", null,
                        t('If you have any questions or need support from your store, please do not hesitate to contact them directly.'),
                        React.createElement("br", null),
                        React.createElement("br", null),
                        t('Thank you and see you soon!')),
                    React.createElement(Button, { label: t('View my quote request'), style: "primary", size: "large", onPress: () => {
                            window.location.href = '/interactive-quote/' + dealId;
                        } }))) : (React.createElement("div", { className: "section sent-section" },
                    React.createElement("h1", null, t('Your Quote Is Ready')),
                    React.createElement("p", null,
                        t('Preliminary quote '),
                        "\u00A0",
                        React.createElement("strong", null, dealNumber),
                        "\u00A0",
                        t(' has been successfully generated and is ready for review.'),
                        React.createElement("br", null),
                        t('A unique link has been sent to your email: '),
                        "\u00A0",
                        React.createElement("strong", null, email)),
                    React.createElement("p", null,
                        t('If further assistance is needed, our team may contact you to finalize it.'),
                        React.createElement("br", null),
                        t("For urgent inquiries, please don't hesitate to reach out directly.")),
                    React.createElement(Button, { label: t('View my quote'), style: "primary", size: "large", onPress: () => {
                            window.location.href = '/interactive-quote/' + dealId;
                        } }))),
                React.createElement("div", { className: "dynamic-sales-form-screen-footer" },
                    React.createElement("div", { className: "dynamic-sales-form-screen-footer--lang-switcher" },
                        React.createElement("a", { href: `${isStoreMode ? '/store' : '/form'}/${language === 'en' ? 'fr' : 'en'}${partnerForm ? `/p/${partnerSlug}` : ''}` },
                            React.createElement(Icon, { value: "globe" }),
                            React.createElement(Text, { value: language === 'fr' ? 'English' : 'Français' }))),
                    React.createElement("div", { className: "dynamic-sales-form-screen-powered" },
                        React.createElement("div", { className: "dynamic-sales-form-screen-powered-logo" },
                            React.createElement("a", { href: "https://centralio.com", target: "_blank", rel: "noreferrer" },
                                React.createElement("div", { className: "dynamic-sales-form-screen-powered-text" },
                                    t('Interactive Quote'),
                                    " \u2122 ",
                                    t('by')),
                                React.createElement("img", { src: "/images/icon.svg" }),
                                React.createElement("span", { className: "dynamic-sales-form-screen-powered-centralio" }, "Centralio")))))))));
}
